import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import moment from 'moment';
import { ColDef, GridApi, GridReadyEvent, RowClassParams, RowClickedEvent } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessService } from '../mess.service';
import {
  MealOrderCampusDetail,
  MealOrderHeadcountSummary,
  MealOrderMatrixRow,
} from '../meal-order-dialog/meal-order-dialog.component';

@Component({
  selector: 'app-meal-order-summary',
  templateUrl: './meal-order-summary.component.html',
  styleUrls: ['./meal-order-summary.component.scss'],
})
export class MealOrderSummaryComponent implements OnInit {
  /** 0 Today, 1 Tomorrow, 2 Custom */
  selectedTabIndex = 0;

  customDate: Date = new Date();

  /** Human-readable label for the active tab’s date */
  activeDateLabel = '';

  /** API date string */
  private activeRequestDate = '';

  rowData: Record<string, unknown>[] = [];
  pinnedBottomRowData: Record<string, unknown>[] = [];

  mealTypeOrder: string[] = [];

  columnDefs: ColDef[] = [];

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 100,
    menuTabs: [],
    suppressHeaderMenuButton: true,
    sortable: true,
    filter: false,
    resizable: true,
    headerClass: 'mos-header-center',
  };

  drawerOpen = false;
  drawerPanelOpen = false;
  selectedDetail: MealOrderCampusDetail | null = null;

  private gridApi?: GridApi;

  /** Last API rows for the active date (used to build the detail matrix). */
  private lastRawRows: Record<string, unknown>[] = [];

  /** API meal keys on nested campus summary objects. */
  private readonly nestedMealApiKeys = ['breakfast', 'lunch', 'snacks', 'dinner'] as const;

  /** Column header names aligned with `mealCounts` keys on grid rows. */
  private readonly nestedMealDisplayKeys = ['Breakfast', 'Lunch', 'Snacks', 'Dinner'] as const;

  overlayLoadingTemplate = `<span class="ag-overlay-loading-center">Please wait while data is loading...</span>`;
  overlayNoRowsTemplate = `<span>No meal orders for this date</span>`;

  constructor(
    private messService: MessService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.syncActiveDateFromTab();
  }

  /**
   * Custom date: any past date is allowed; dates **after tomorrow** are disabled
   * (i.e. today and tomorrow remain selectable; the day after tomorrow onward is blocked).
   */
  get summaryMaxDate(): Date {
    return moment().add(1, 'day').startOf('day').toDate();
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    if (index === 2) {
      this.customDate = this.clampDateToSelectableRange(this.customDate);
    }
    this.syncActiveDateFromTab();
    this.loadGridData();
  }

  onCustomDateChange(): void {
    if (this.selectedTabIndex !== 2) {
      return;
    }
    this.customDate = this.clampDateToSelectableRange(this.customDate);
    this.syncActiveDateFromTab();
    this.loadGridData();
  }

  private clampDateToSelectableRange(d: Date): Date {
    const day = moment(d).startOf('day');
    const max = moment().add(1, 'day').startOf('day');
    if (day.isAfter(max)) {
      return max.toDate();
    }
    return day.toDate();
  }

  onGridReady(ev: GridReadyEvent): void {
    this.gridApi = ev.api;
    this.loadGridData();
  }

  onRowClicked(ev: RowClickedEvent): void {
    if (!ev.data || ev.node?.rowPinned) {
      return;
    }
    const row = ev.data as Record<string, unknown>;
    if (!this.rowHasMealOrderData(row)) {
      return;
    }
    const campusName = String(row['campusName'] ?? '');
    const mealCounts = (row['mealCounts'] as Record<string, number>) ?? {};
    const headcounts: MealOrderHeadcountSummary = {
      studentCount: Number(row['studentCount'] ?? 0),
      employeeCount: Number(row['employeeCount'] ?? 0),
      subStaffCount: Number(row['subStaffCount'] ?? 0),
      visitorsCount: Number(row['visitorsCount'] ?? 0),
    };
    const mealHeads = this.mealTypeOrder
      .map((mealType) => ({
        mealType,
        heads: Number(mealCounts[mealType] ?? 0),
      }))
      .filter((m) => m.heads > 0);
    const { matrix, matrixTotals } = this.buildMealMatrixForCampus(campusName, headcounts);
    const dateLabel =
      String(row['requestDate'] ?? '').trim() || this.activeDateLabel;
    const contact = this.pickContactForCampus(campusName);
    const requestedBy = this.trimStr(row['requestedBy']) ?? contact.requestedBy;
    const requestedMobileNo = this.trimStr(row['requestedMobileNo']) ?? contact.requestedMobileNo;
    const requestedUsername = this.trimStr(row['requestedUsername']) ?? contact.requestedUsername;
    const requestedPayrollId = this.trimStr(row['requestedPayrollId']) ?? contact.requestedPayrollId;
    this.selectedDetail = {
      campusName,
      dateLabel,
      headcounts,
      mealHeads,
      matrix,
      matrixTotals,
      requestedBy,
      requestedMobileNo,
      requestedUsername,
      requestedPayrollId,
      callPhone: requestedMobileNo,
    };
    this.openDrawer();
  }

  private trimStr(value: unknown): string | undefined {
    const s = String(value ?? '').trim();
    return s ? s : undefined;
  }

  /** Resolve contact fields from the raw API list when the grid row omits them. */
  private pickContactForCampus(campusName: string): {
    requestedBy?: string;
    requestedMobileNo?: string;
    requestedUsername?: string;
    requestedPayrollId?: string;
  } {
    const nested = this.lastRawRows.find(
      (r) => this.isNestedCampusRow(r) && String(r['campusName'] ?? '') === campusName,
    );
    if (nested) {
      return {
        requestedBy: this.trimStr(nested['requestedBy']),
        requestedMobileNo: this.trimStr(nested['requestedMobileNo']),
        requestedUsername: this.trimStr(nested['requestedUsername']),
        requestedPayrollId: this.trimStr(nested['requestedPayrollId']),
      };
    }
    for (const r of this.lastRawRows) {
      if (this.isNestedCampusRow(r)) {
        continue;
      }
      const c = String(r?.['kitchenName'] ?? r?.['buildingName'] ?? '').trim() || 'Unknown';
      if (c !== campusName) {
        continue;
      }
      const rb = this.trimStr(r['requestedBy']);
      const rm = this.trimStr(r['requestedMobileNo']);
      const ru = this.trimStr(r['requestedUsername']);
      const rp = this.trimStr(r['requestedPayrollId']);
      if (rb || rm || ru || rp) {
        return {
          requestedBy: rb,
          requestedMobileNo: rm,
          requestedUsername: ru,
          requestedPayrollId: rp,
        };
      }
    }
    return {};
  }

  closeDrawer(): void {
    this.drawerPanelOpen = false;
    window.setTimeout(() => {
      this.drawerOpen = false;
      this.selectedDetail = null;
      this.cdr.markForCheck();
    }, 280);
  }

  private openDrawer(): void {
    this.drawerOpen = true;
    this.drawerPanelOpen = false;
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.drawerPanelOpen = true;
      this.cdr.detectChanges();
    });
  }

  private syncActiveDateFromTab(): void {
    let d: moment.Moment;
    if (this.selectedTabIndex === 0) {
      d = moment().startOf('day');
    } else if (this.selectedTabIndex === 1) {
      d = moment().add(1, 'day').startOf('day');
    } else {
      d = moment(this.customDate).startOf('day');
    }
    this.activeRequestDate = d.format('DD MMM YYYY');
    this.activeDateLabel = this.activeRequestDate;
  }

  private loadGridData(): void {
    const date = this.activeRequestDate || moment().format('DD MMM YYYY');
    this.gridApi?.showLoadingOverlay();
    this.spinner.show();
    this.messService.mealOrderSummary(date).subscribe({
      next: (res: unknown) => {
        const rows = Array.isArray(res) ? res : (Array.isArray((res as { data?: unknown[] })?.data) ? (res as { data: unknown[] }).data : []);
        this.lastRawRows = (rows as Record<string, unknown>[]) ?? [];
        const { gridRows, mealTypes } = this.isNestedCampusSummaryFormat(this.lastRawRows)
          ? this.mapNestedCampusSummariesToGrid(this.lastRawRows)
          : this.pivotHistoryRows(this.lastRawRows);
        this.mealTypeOrder = mealTypes;
        this.rebuildColumnDefs(mealTypes);
        this.rowData = gridRows;
        this.updatePinnedBottomRow();
        this.spinner.hide();
        if (!gridRows.length) {
          this.gridApi?.showNoRowsOverlay();
        } else {
          this.gridApi?.hideOverlay();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.lastRawRows = [];
        this.rowData = [];
        this.pinnedBottomRowData = [];
        this.mealTypeOrder = [];
        this.rebuildColumnDefs([]);
        this.spinner.hide();
        this.gridApi?.showNoRowsOverlay();
        this.cdr.markForCheck();
      },
    });
  }

  /** Nested shape: campusName + breakfast/lunch/snacks/dinner blocks. */
  private isNestedCampusRow(r: Record<string, unknown> | undefined): boolean {
    if (!r || typeof r !== 'object') {
      return false;
    }
    const b = r['breakfast'];
    return (
      typeof r['campusName'] === 'string' &&
      typeof b === 'object' &&
      b !== null &&
      'studentCount' in (b as object)
    );
  }

  private isNestedCampusSummaryFormat(rows: Record<string, unknown>[]): boolean {
    return rows.some((r) => this.isNestedCampusRow(r));
  }

  private countInNestedMealBlock(block: unknown, field: string): number {
    if (!block || typeof block !== 'object') {
      return 0;
    }
    return Number((block as Record<string, unknown>)[field] ?? 0);
  }

  private sumNestedMealBlockHeads(block: unknown): number {
    return (
      this.countInNestedMealBlock(block, 'studentCount') +
      this.countInNestedMealBlock(block, 'employeeCount') +
      this.countInNestedMealBlock(block, 'subStaffCount') +
      this.countInNestedMealBlock(block, 'visitorsCount')
    );
  }

  private mapNestedCampusSummariesToGrid(rows: Record<string, unknown>[]): {
    gridRows: Record<string, unknown>[];
    mealTypes: string[];
  } {
    const mealTypes = [...this.nestedMealDisplayKeys];
    const gridRows = rows
      .filter((r) => this.isNestedCampusRow(r))
      .map((r) => {
        const mealCounts: Record<string, number> = {};
        for (let i = 0; i < this.nestedMealApiKeys.length; i++) {
          const apiKey = this.nestedMealApiKeys[i];
          const displayKey = this.nestedMealDisplayKeys[i];
          mealCounts[displayKey] = this.sumNestedMealBlockHeads(r[apiKey]);
        }
        let studentCount = 0;
        let employeeCount = 0;
        let subStaffCount = 0;
        let visitorsCount = 0;
        for (const apiKey of this.nestedMealApiKeys) {
          const block = r[apiKey];
          studentCount += this.countInNestedMealBlock(block, 'studentCount');
          employeeCount += this.countInNestedMealBlock(block, 'employeeCount');
          subStaffCount += this.countInNestedMealBlock(block, 'subStaffCount');
          visitorsCount += this.countInNestedMealBlock(block, 'visitorsCount');
        }
        return {
          campusName: String(r['campusName'] ?? ''),
          campusId: r['campusId'],
          requestDate: r['requestDate'],
          mealCounts,
          studentCount,
          employeeCount,
          subStaffCount,
          visitorsCount,
          requestedBy: this.trimStr(r['requestedBy']),
          requestedMobileNo: this.trimStr(r['requestedMobileNo']),
          requestedUsername: this.trimStr(r['requestedUsername']),
          requestedPayrollId: this.trimStr(r['requestedPayrollId']),
        };
      });
    return { gridRows, mealTypes };
  }

  private pivotHistoryRows(rows: Record<string, unknown>[]): {
    gridRows: Record<string, unknown>[];
    mealTypes: string[];
  } {
    const byCampus = new Map<
      string,
      {
        campusName: string;
        mealCounts: Map<string, number>;
        studentCount: number;
        employeeCount: number;
        subStaffCount: number;
        visitorsCount: number;
        requestedBy?: string;
        requestedMobileNo?: string;
        requestedUsername?: string;
        requestedPayrollId?: string;
      }
    >();

    for (const r of rows) {
      const campusRaw = String(r?.['kitchenName'] ?? r?.['buildingName'] ?? '').trim();
      const campusName = campusRaw || 'Unknown';
      const meal = String(r?.['mealType'] ?? r?.['scenarioName'] ?? 'Meal').trim() || 'Meal';
      const heads = Number(r?.['totalHeads'] ?? 0);

      if (!byCampus.has(campusName)) {
        byCampus.set(campusName, {
          campusName,
          mealCounts: new Map(),
          studentCount: 0,
          employeeCount: 0,
          subStaffCount: 0,
          visitorsCount: 0,
          requestedBy: this.trimStr(r?.['requestedBy']),
          requestedMobileNo: this.trimStr(r?.['requestedMobileNo']),
          requestedUsername: this.trimStr(r?.['requestedUsername']),
          requestedPayrollId: this.trimStr(r?.['requestedPayrollId']),
        });
      }
      const agg = byCampus.get(campusName)!;
      agg.mealCounts.set(meal, (agg.mealCounts.get(meal) ?? 0) + heads);
      agg.studentCount += Number(r?.['studentCount'] ?? 0);
      agg.employeeCount += Number(r?.['employeeCount'] ?? 0);
      agg.subStaffCount += Number(r?.['subStaffCount'] ?? 0);
      agg.visitorsCount += Number(r?.['visitorsCount'] ?? 0);
    }

    const mealTypeSet = new Set<string>();
    for (const v of byCampus.values()) {
      v.mealCounts.forEach((_, k) => mealTypeSet.add(k));
    }
    const mealTypes = Array.from(mealTypeSet).sort((a, b) => a.localeCompare(b));

    const gridRows = Array.from(byCampus.values()).map((agg) => ({
      campusName: agg.campusName,
      mealCounts: Object.fromEntries(agg.mealCounts),
      studentCount: agg.studentCount,
      employeeCount: agg.employeeCount,
      subStaffCount: agg.subStaffCount,
      visitorsCount: agg.visitorsCount,
      requestedBy: agg.requestedBy,
      requestedMobileNo: agg.requestedMobileNo,
      requestedUsername: agg.requestedUsername,
      requestedPayrollId: agg.requestedPayrollId,
    }));

    return { gridRows, mealTypes };
  }

  private rebuildColumnDefs(mealTypes: string[]): void {
    const countFmt = (p: { value: unknown }): string => this.formatCount(p.value);
    const cols: ColDef[] = [
      {
        field: 'campusName',
        headerName: 'Campus',
        flex: 1.4,
        minWidth: 350,
        pinned: 'left',
        cellRenderer: (params: { node?: { rowPinned?: string }; value?: unknown }) => {
          if (params.node?.rowPinned) {
            const bold = document.createElement('strong');
            bold.innerText = 'Grand Total';
            bold.style.fontSize = '14px';
            return bold;
          }
          return params.value ?? '';
        },
      },
      ...mealTypes.map(
        (mt): ColDef => ({
          colId: `meal_${mt}`,
          headerName: mt,
          minWidth: 120,
          valueGetter: (p) => Number((p.data?.['mealCounts'] as Record<string, number>)?.[mt] ?? 0),
          valueFormatter: countFmt,
          cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
        }),
      ),
      {
        colId: 'chevron',
        headerName: '',
        width: 48,
        maxWidth: 52,
        flex: 0,
        sortable: false,
        suppressMovable: true,
        lockPosition: 'right',
        pinned: 'right',
        cellClass: 'mos-grid-chevron-cell',
        valueGetter: () => '',
        cellRenderer: (params: { node?: { rowPinned?: string }; data?: Record<string, unknown> }) => {
          if (params.node?.rowPinned) {
            return '';
          }
          if (!this.rowHasMealOrderData(params.data ?? {})) {
            return '';
          }
          const el = document.createElement('span');
          el.className = 'mos-grid-chevron';
          el.textContent = '›';
          el.setAttribute('aria-hidden', 'true');
          return el;
        },
      },
    ];
    this.columnDefs = cols;
  }

  /** Row is clickable only when at least one meal type (Breakfast/Lunch/Snacks/Dinner) has heads. */
  private rowHasMealOrderData(row: Record<string, unknown>): boolean {
    return this.getRowMealTotal(row) > 0;
  }

  private getRowMealTotal(row: Record<string, unknown>): number {
    const mealCounts = (row['mealCounts'] as Record<string, number>) ?? {};
    const types = this.mealTypeOrder.length ? this.mealTypeOrder : [...this.nestedMealDisplayKeys];
    return types.reduce((sum, mt) => sum + Number(mealCounts[mt] ?? 0), 0);
  }

  getRowClass = (params: RowClassParams): string => {
    if (params.node?.rowPinned) {
      return '';
    }
    const row = (params.data ?? {}) as Record<string, unknown>;
    return this.rowHasMealOrderData(row) ? '' : 'mos-grid-row--no-click';
  };

  private updatePinnedBottomRow(): void {
    if (!this.rowData?.length) {
      this.pinnedBottomRowData = [];
      return;
    }
    const mealCounts: Record<string, number> = {};
    for (const mt of this.mealTypeOrder) {
      mealCounts[mt] = this.rowData.reduce((s, r) => {
        const mc = (r['mealCounts'] as Record<string, number>) ?? {};
        return s + Number(mc[mt] ?? 0);
      }, 0);
    }
    this.pinnedBottomRowData = [{ campusName: 'Grand Total', mealCounts }];
  }

  private formatCount(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const n = Number(String(value).replace(/,/g, ''));
    if (isNaN(n)) {
      return '';
    }
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(n);
  }

  private normalizeMealBucket(mt: string): 'breakfast' | 'lunch' | 'snacks' | 'dinner' | null {
    const x = String(mt || '')
      .trim()
      .toLowerCase();
    if (!x) {
      return null;
    }
    if (x.includes('break') || x === 'bf' || x.includes('morning')) {
      return 'breakfast';
    }
    if (x.includes('lunch')) {
      return 'lunch';
    }
    if (x.includes('snack')) {
      return 'snacks';
    }
    if (x.includes('dinner') || x.includes('evening')) {
      return 'dinner';
    }
    return null;
  }

  private buildMealMatrixForCampus(
    campusName: string,
    fallback: MealOrderHeadcountSummary,
  ): { matrix: MealOrderMatrixRow[]; matrixTotals: MealOrderMatrixRow } {
    const nestedCampus = this.lastRawRows.find(
      (r) => this.isNestedCampusRow(r) && String(r['campusName'] ?? '') === campusName,
    );
    if (nestedCampus) {
      return this.buildMealMatrixFromNestedCampus(nestedCampus);
    }

    type Bucket = 'breakfast' | 'lunch' | 'snacks' | 'dinner';
    type Acc = Record<Bucket, number> & { unmapped: number };

    const emptyAcc = (): Acc => ({
      breakfast: 0,
      lunch: 0,
      snacks: 0,
      dinner: 0,
      unmapped: 0,
    });

    const filtered = this.lastRawRows.filter((r) => {
      const c = String(r?.['kitchenName'] ?? r?.['buildingName'] ?? '').trim() || 'Unknown';
      return c === campusName;
    });

    if (!filtered.length) {
      const matrix: MealOrderMatrixRow[] = [
        {
          label: 'Students',
          heads: Number(fallback.studentCount ?? 0),
          breakfast: 0,
          lunch: 0,
          snacks: 0,
          dinner: 0,
        },
        {
          label: 'Staff',
          heads: Number(fallback.employeeCount ?? 0),
          breakfast: 0,
          lunch: 0,
          snacks: 0,
          dinner: 0,
        },
        {
          label: 'Sub-Staff',
          heads: Number(fallback.subStaffCount ?? 0),
          breakfast: 0,
          lunch: 0,
          snacks: 0,
          dinner: 0,
        },
        {
          label: 'Others',
          heads: Number(fallback.visitorsCount ?? 0),
          breakfast: 0,
          lunch: 0,
          snacks: 0,
          dinner: 0,
        },
      ];
      const matrixTotals = this.sumMatrixRows(matrix);
      return { matrix, matrixTotals };
    }

    const student = emptyAcc();
    const employee = emptyAcc();
    const subStaff = emptyAcc();
    const visitors = emptyAcc();

    const add = (acc: Acc, bucket: Bucket | null, val: number): void => {
      const n = Number(val ?? 0);
      if (!n) {
        return;
      }
      if (!bucket) {
        acc.unmapped += n;
        return;
      }
      acc[bucket] += n;
    };

    for (const r of filtered) {
      const b = this.normalizeMealBucket(String(r['mealType'] ?? r['scenarioName'] ?? ''));
      add(student, b, Number(r['studentCount'] ?? 0));
      add(employee, b, Number(r['employeeCount'] ?? 0));
      add(subStaff, b, Number(r['subStaffCount'] ?? 0));
      add(visitors, b, Number(r['visitorsCount'] ?? 0));
    }

    const rowFrom = (label: string, acc: Acc): MealOrderMatrixRow => ({
      label,
      heads: acc.breakfast + acc.lunch + acc.snacks + acc.dinner + acc.unmapped,
      breakfast: acc.breakfast,
      lunch: acc.lunch,
      snacks: acc.snacks,
      dinner: acc.dinner,
    });

    const matrix: MealOrderMatrixRow[] = [
      rowFrom('Students', student),
      rowFrom('Staff', employee),
      rowFrom('Sub-Staff', subStaff),
      rowFrom('Others', visitors),
    ];
    const matrixTotals = this.sumMatrixRows(matrix);
    return { matrix, matrixTotals };
  }

  private buildMealMatrixFromNestedCampus(
    nested: Record<string, unknown>,
  ): { matrix: MealOrderMatrixRow[]; matrixTotals: MealOrderMatrixRow } {
    const g = (meal: (typeof this.nestedMealApiKeys)[number], field: string): number =>
      this.countInNestedMealBlock(nested[meal], field);

    const students: MealOrderMatrixRow = {
      label: 'Students',
      breakfast: g('breakfast', 'studentCount'),
      lunch: g('lunch', 'studentCount'),
      snacks: g('snacks', 'studentCount'),
      dinner: g('dinner', 'studentCount'),
      heads: 0,
    };
    students.heads = students.breakfast + students.lunch + students.snacks + students.dinner;

    const staff: MealOrderMatrixRow = {
      label: 'Staff',
      breakfast: g('breakfast', 'employeeCount'),
      lunch: g('lunch', 'employeeCount'),
      snacks: g('snacks', 'employeeCount'),
      dinner: g('dinner', 'employeeCount'),
      heads: 0,
    };
    staff.heads = staff.breakfast + staff.lunch + staff.snacks + staff.dinner;

    const subStaff: MealOrderMatrixRow = {
      label: 'Sub-Staff',
      breakfast: g('breakfast', 'subStaffCount'),
      lunch: g('lunch', 'subStaffCount'),
      snacks: g('snacks', 'subStaffCount'),
      dinner: g('dinner', 'subStaffCount'),
      heads: 0,
    };
    subStaff.heads = subStaff.breakfast + subStaff.lunch + subStaff.snacks + subStaff.dinner;

    const others: MealOrderMatrixRow = {
      label: 'Others',
      breakfast: g('breakfast', 'visitorsCount'),
      lunch: g('lunch', 'visitorsCount'),
      snacks: g('snacks', 'visitorsCount'),
      dinner: g('dinner', 'visitorsCount'),
      heads: 0,
    };
    others.heads = others.breakfast + others.lunch + others.snacks + others.dinner;

    const matrix = [students, staff, subStaff, others];
    return { matrix, matrixTotals: this.sumMatrixRows(matrix) };
  }

  private sumMatrixRows(rows: MealOrderMatrixRow[]): MealOrderMatrixRow {
    const z = (k: keyof Omit<MealOrderMatrixRow, 'label'>): number =>
      rows.reduce((s, r) => s + Number(r[k] ?? 0), 0);
    return {
      label: 'Total',
      heads: z('heads'),
      breakfast: z('breakfast'),
      lunch: z('lunch'),
      snacks: z('snacks'),
      dinner: z('dinner'),
    };
  }
}
